
import { useEffect, useState } from "react";
import { CustomizerConfig } from "@/config/customizerConfig";
import { useMapStore } from "@/store/useMapStore";
import { useShopifyStore } from "@/hooks/useShopifyStore";
import { useIsMobile } from "@/hooks/use-mobile";
import { Loader2 } from "lucide-react";
import MapTab from "./tabs/MapTab";
import PaletteTab from "./tabs/PaletteTab";
import SizeTab from "./tabs/SizeTab";
import FramesTab from "./tabs/FramesTab";
import LabelsTab from "./tabs/LabelsTab";
import AdvancedTab from "./tabs/AdvancedTab";

interface ControlPanelProps {
  config: CustomizerConfig;
}

const ControlPanel = ({ config }: ControlPanelProps) => {
  const { previewState, startPreview, location, activePrice, previewTab } = useMapStore();
  const { addToCart } = useShopifyStore();
  const isMobile = useIsMobile();

  // Get active tab from URL or default to 'map'
  const getActiveTab = () => {
    const hash = window.location.hash;
    const match = hash.match(/\/editor\/(\w+)/);
    return match ? match[1] : 'map';
  };

  const [activeTab, setActiveTab] = useState(getActiveTab());

  useEffect(() => {
    // Listen for hash changes
    const handleHashChange = () => {
      setActiveTab(getActiveTab()); // Update state instead of reloading
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const renderContent = () => {
    switch (activeTab) {
      case "map":
      case "location":
        return <MapTab config={config} />;
      case "labels":
        return <LabelsTab config={config} />;
      case "palette":
        return <PaletteTab />;
      case "size":
        return <SizeTab config={config} />;
      case "frames":
        return <FramesTab config={config} />;
      case "icons":
        return (
          <div className="p-6">
            <h3 className="text-lg mb-4" style={{ fontFamily: "var(--font-heading)", color: "var(--brand-text)" }}>Icons</h3>
            <p className="text-sm" style={{ color: "var(--brand-muted)" }}>Add special icons to mark memorable locations</p>
          </div>
        );
      case "advanced":
        return <AdvancedTab config={config} />;
      default:
        return <MapTab config={config} />;
    }
  };

  const isGenerating = previewState === "loading";
  const canGenerate = !!location && !isGenerating;
  // v3.13.0: Hide Generate CTA when user is on 3D tab (desktop only)
  // On mobile, PreviewPanel doesn't exist, so always show the button
  const hideGenerateCTA = !isMobile && previewTab === '3d';

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto scrollbar-custom">
        {renderContent()}
      </div>

      {/* CTA Stack: Generate + Add to Cart (matching HTML spec) */}
      <div className="flex-shrink-0 p-6 space-y-3">
        {/* Primary: Generate 3D Preview — hidden on 3D tab (desktop) since CTA is inside the tab */}
        {!hideGenerateCTA && (
          <button
            onClick={startPreview}
            disabled={!canGenerate}
            className="w-full py-4 text-white rounded-xl text-sm font-semibold tracking-wide transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: "var(--sage-400)" }}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) {
                e.currentTarget.style.backgroundColor = "var(--sage-500)";
                e.currentTarget.style.boxShadow = "0 4px 20px rgba(123, 158, 135, 0.35)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "var(--sage-400)";
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              "Generate 3D Preview"
            )}
          </button>
        )}

        {/* Secondary: Add to Cart */}
        <button
          onClick={() => addToCart()}
          className="w-full py-3.5 rounded-xl text-sm font-medium tracking-wide border transition-colors duration-300 flex items-center justify-center gap-2"
          style={{
            borderColor: "var(--sage-300)",
            color: "var(--sage-600)",
            backgroundColor: "transparent",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--sage-50)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          Add to Cart{activePrice !== null ? <> &mdash; ${activePrice.toFixed(2)}</> : null}
        </button>

        {!hideGenerateCTA && (
          <p
            className="text-center text-xs"
            style={{ color: "var(--brand-light)" }}
          >
            3D preview is free &mdash; or skip and order directly
          </p>
        )}
      </div>
    </div>
  );
};

export default ControlPanel;
